import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { InvitationPreview } from '../components/InvitationPreview';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  Edit,
  Trash2,
  ShoppingBag,
  Copy,
  RefreshCw,
  Sparkles,
  Download,
  CreditCard,
} from 'lucide-react';
import { API_URL } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { copyToClipboard, formatDate, formatPrice, orderNumberLabel } from '../utils/format';
import { getErrorMessage } from '../utils/errors';
import { useDocumentTitle } from '../hooks/use-document-title';

// Status chips share one soft palette across invitations and orders.
const CHIP = {
  paid: 'bg-[#E6EEE3] text-[#2F4429]',
  waiting: 'bg-[#F6EBD2] text-[#6B4E0E]',
  neutral: 'bg-[#EEEBE4] text-[#4A4640]',
  danger: 'bg-red-50 text-red-800',
};

const ORDER_CHIP = {
  paid: CHIP.paid,
  completed: CHIP.paid,
  shipped: CHIP.paid,
  pending: CHIP.waiting,
  processing: CHIP.waiting,
  cancelled: CHIP.neutral,
  expired: CHIP.neutral,
  failed: CHIP.danger,
};

const INVITATION_STATUS = {
  paid: { label: 'Paid', chip: CHIP.paid },
  waiting: { label: 'Waiting for payment', chip: CHIP.waiting },
  draft: { label: 'Draft', chip: CHIP.neutral },
};

// Pill buttons: ink for the main action, thin gradient outline for the rest.
const PILL = 'h-10 rounded-full px-3.5 sm:px-4 text-sm font-bold';
const PILL_INK = `${PILL} bg-ink text-paper hover:bg-ink/90`;
const PILL_OUTLINE = `${PILL} gradient-border text-ink shadow-none hover:[--gb-bg:#F2F0EB]`;

const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '');

const StatusChip = ({ className, children }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold whitespace-nowrap ${className}`}>
    {children}
  </span>
);

const StatTile = ({ label, value, loading }) => (
  <div className="gradient-border rounded-[18px] px-3.5 py-3.5 sm:px-6 sm:py-5 flex flex-col gap-1 min-w-0">
    <div className="text-xs sm:text-sm leading-snug text-[#4A4640]">{label}</div>
    {loading ? (
      <Skeleton className="h-9 w-12 my-0.5 bg-ink/10" />
    ) : (
      <div className="font-serif text-2xl sm:text-4xl font-semibold text-ink leading-tight mt-auto">{value}</div>
    )}
  </div>
);

const InvitationRowSkeleton = () => (
  <div className="bg-white border border-[#E6E1D6] rounded-[18px] p-4 sm:p-5 flex gap-4 sm:gap-6 items-center" aria-hidden="true">
    <Skeleton className="w-16 sm:w-[76px] aspect-[8.5/11] shrink-0 rounded-sm bg-ink/10" />
    <div className="flex-1 space-y-3">
      <Skeleton className="h-5 w-1/2 bg-ink/10" />
      <Skeleton className="h-4 w-1/3 bg-ink/10" />
    </div>
    <Skeleton className="hidden md:block h-10 w-40 rounded-full bg-ink/10" />
  </div>
);

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="text-center py-14 px-6 bg-cream rounded-[28px]">
    <div className="w-14 h-14 mx-auto mb-5 rounded-full gradient-border gb-cream flex items-center justify-center">
      <Icon className="h-6 w-6 text-ink" aria-hidden="true" />
    </div>
    <h2 className="font-serif text-2xl font-semibold text-ink mb-2">{title}</h2>
    <p className="text-[#4A4640] mb-6 max-w-md mx-auto">{description}</p>
    {action}
  </div>
);

export default function DashboardPage() {
  useDocumentTitle('My invitations');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart } = useCart();
  const [invitations, setInvitations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [invRes, ordRes, tmplRes] = await Promise.all([
        axios.get(`${API_URL}/invitations`),
        axios.get(`${API_URL}/orders`),
        axios.get(`${API_URL}/templates`).catch(() => ({ data: [] })),
      ]);
      setInvitations(Array.isArray(invRes.data) ? invRes.data : []);
      setOrders(Array.isArray(ordRes.data) ? ordRes.data : []);
      setTemplates(Array.isArray(tmplRes.data) ? tmplRes.data : []);
    } catch (err) {
      setError(getErrorMessage(err, 'We could not load your invitations.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API_URL}/invitations/${id}`);
      setInvitations((prev) => prev.filter((inv) => inv.id !== id));
      toast.success('Invitation deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'We could not delete this invitation.'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopyShareLink = async (shareLink) => {
    const url = `${window.location.origin}/share/${shareLink}`;
    if (await copyToClipboard(url)) {
      toast.success('Share link copied to clipboard');
    } else {
      toast.info(`Share link: ${url}`, { duration: 10000 });
    }
  };

  const templatesById = Object.fromEntries(templates.map((t) => [t.id, t]));
  const firstName = user?.name ? user.name.split(' ')[0] : '';

  // An unpaid invitation is "waiting for payment" once it's in the cart or a
  // pending order; otherwise it's still a draft.
  const awaitingPayment = new Set(
    [
      ...(cart?.items || []).map((item) => item.invitation_id || item.invitation?.id),
      ...orders
        .filter((order) => (order.status || 'pending') === 'pending')
        .flatMap((order) => (order.items || []).map((item) => item.invitation_id || item.invitation?.id)),
    ].filter(Boolean)
  );
  const statusOf = (invitation) =>
    invitation.is_paid ? 'paid' : awaitingPayment.has(invitation.id) ? 'waiting' : 'draft';
  const paidCount = invitations.filter((inv) => inv.is_paid).length;
  const waitingCount = invitations.filter((inv) => statusOf(inv) === 'waiting').length;

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <Navbar />

      <main id="main-content" className="flex-1 pt-28 sm:pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-5 mb-8"
          >
            <div className="min-w-0">
              <p className="text-[13px] font-bold uppercase tracking-[0.16em] text-[#6E5B1C] mb-2">
                {firstName ? `Welcome, ${firstName}` : 'Your dashboard'}
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-ink">
                My <span className="italic font-medium">invitations</span>
              </h1>
              <p className="text-[#4A4640] mt-2 max-w-xl">
                Invitations you made online, by phone or by text message all appear here.
              </p>
            </div>
            <Button asChild className={`${PILL_INK} h-12 px-6 text-[15px] self-start sm:self-auto shrink-0`}>
              <Link to="/templates" data-testid="create-new-btn">
                <Plus className="h-4 w-4" />
                New invitation
              </Link>
            </Button>
          </motion.div>

          {!error && (
            <div className="grid grid-cols-3 gap-2.5 sm:gap-5 mb-10" aria-label="Summary">
              <StatTile label="Invitations" value={invitations.length} loading={loading} />
              <StatTile label="Waiting for payment" value={waitingCount} loading={loading} />
              <StatTile label="Paid" value={paidCount} loading={loading} />
            </div>
          )}

          {error ? (
            <div className="text-center py-14 px-6 bg-cream rounded-[28px]" role="alert">
              <p className="text-[#4A4640] mb-5">{error}</p>
              <Button variant="ghost" onClick={fetchData} className={PILL_OUTLINE + ' gb-cream'}>
                <RefreshCw className="h-4 w-4" />
                Try again
              </Button>
            </div>
          ) : (
            <Tabs defaultValue="invitations" className="space-y-6">
              <TabsList className="bg-cream rounded-full h-11 p-1">
                <TabsTrigger
                  value="invitations"
                  className="rounded-full h-9 px-4 font-semibold data-[state=active]:bg-ink data-[state=active]:text-paper"
                  data-testid="tab-invitations"
                >
                  Invitations{!loading && ` (${invitations.length})`}
                </TabsTrigger>
                <TabsTrigger
                  value="orders"
                  className="rounded-full h-9 px-4 font-semibold data-[state=active]:bg-ink data-[state=active]:text-paper"
                  data-testid="tab-orders"
                >
                  Orders{!loading && ` (${orders.length})`}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="invitations" className="mt-0">
                {loading ? (
                  <div className="space-y-4" aria-busy="true">
                    {[0, 1, 2].map((i) => (
                      <InvitationRowSkeleton key={i} />
                    ))}
                  </div>
                ) : invitations.length === 0 ? (
                  <EmptyState
                    icon={Sparkles}
                    title="No invitations yet"
                    description="Pick a design, personalize the wording and colors, and your first invitation will appear here."
                    action={
                      <Button asChild className={PILL_INK}>
                        <Link to="/templates">Browse designs</Link>
                      </Button>
                    }
                  />
                ) : (
                  <ul className="space-y-4" aria-label="Your invitations">
                    {invitations.map((invitation, index) => {
                      const template = templatesById[invitation.template_id];
                      const status = statusOf(invitation);
                      const statusInfo = INVITATION_STATUS[status];
                      const editorPath = `/editor/invitation/${invitation.id}`;
                      const date = formatDate(invitation.updated_at || invitation.created_at);
                      return (
                        <motion.li
                          key={invitation.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: Math.min(index, 9) * 0.05 }}
                          className="bg-white border border-[#E6E1D6] rounded-[18px] p-4 sm:p-5 transition-shadow hover:shadow-[0_18px_40px_-28px_rgba(26,26,26,0.35)]"
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
                            <div className="flex items-center gap-4 sm:gap-5 flex-1 min-w-0">
                              <Link
                                to={editorPath}
                                className="w-16 sm:w-[76px] shrink-0 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2"
                                aria-label={`Edit ${invitation.name}`}
                              >
                                <InvitationPreview
                                  backgroundImage={template?.display_image || template?.background_image || template?.thumbnail_image}
                                  fields={invitation.text_fields}
                                  elements={invitation.elements}
                                  background={invitation.background}
                                  perspective={template?.perspective}
                                  alt={`${invitation.name} preview`}
                                  className="w-full rounded-[3px] bg-white shadow-[0_8px_16px_-8px_rgba(26,26,26,0.4)]"
                                />
                              </Link>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                                  <h2 className="font-bold text-base sm:text-lg text-ink truncate max-w-full">
                                    {invitation.name}
                                  </h2>
                                  <StatusChip className={statusInfo.chip}>{statusInfo.label}</StatusChip>
                                </div>
                                <p className="text-sm text-[#6B665C] mt-1 truncate">
                                  {invitation.order_number && (
                                    <span className="font-medium text-ink" data-testid={`order-number-${invitation.id}`}>
                                      {orderNumberLabel(invitation.order_number)} ·{' '}
                                    </span>
                                  )}
                                  {template?.name || 'Invitation'}
                                  {date ? ` · ${date}` : ''}
                                </p>
                              </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-2 lg:shrink-0 lg:w-[372px] lg:justify-end">
                              <Button
                                variant="ghost"
                                onClick={() => navigate(editorPath)}
                                className={status === 'draft' ? PILL_INK : PILL_OUTLINE}
                                data-testid={`edit-invitation-${invitation.id}`}
                              >
                                <Edit className="h-4 w-4" />
                                {status === 'draft' ? 'Continue' : 'Edit'}
                              </Button>
                              {status === 'paid' && (
                                <Button asChild className={PILL_INK}>
                                  <Link to={editorPath}>
                                    <Download className="h-4 w-4" />
                                    Download
                                  </Link>
                                </Button>
                              )}
                              {status === 'waiting' && (
                                <Button asChild className={PILL_INK}>
                                  <Link to="/cart">
                                    <CreditCard className="h-4 w-4" />
                                    Pay now
                                  </Link>
                                </Button>
                              )}
                              {invitation.share_link && (
                                <Button
                                  variant="ghost"
                                  onClick={() => handleCopyShareLink(invitation.share_link)}
                                  className={PILL_OUTLINE}
                                  data-testid={`share-invitation-${invitation.id}`}
                                >
                                  <Copy className="h-4 w-4" />
                                  Share
                                </Button>
                              )}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-10 w-10 rounded-full text-[#6B665C] hover:text-destructive hover:bg-destructive/10 ml-auto lg:ml-0"
                                    disabled={deletingId === invitation.id}
                                    aria-label={`Delete ${invitation.name}`}
                                    data-testid={`delete-invitation-${invitation.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="rounded-[24px]">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle className="font-serif text-2xl font-semibold">
                                      Delete “{invitation.name}”?
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This action cannot be undone. The invitation and its share link will stop working.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter className="gap-2">
                                    <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(invitation.id)}
                                      className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        </motion.li>
                      );
                    })}
                  </ul>
                )}
              </TabsContent>

              <TabsContent value="orders" className="mt-0">
                {loading ? (
                  <div className="space-y-4" aria-busy="true">
                    {[0, 1].map((i) => (
                      <InvitationRowSkeleton key={i} />
                    ))}
                  </div>
                ) : orders.length === 0 ? (
                  <EmptyState
                    icon={ShoppingBag}
                    title="No orders yet"
                    description="When you purchase an invitation, your receipt and download access will show up here."
                    action={
                      <Button asChild variant="ghost" className={PILL_OUTLINE + ' gb-cream'}>
                        <Link to="/cart">View cart</Link>
                      </Button>
                    }
                  />
                ) : (
                  <ul className="space-y-4" aria-label="Your orders">
                    {orders.map((order, index) => {
                      const firstItem = order.items?.[0];
                      const orderInvitation = firstItem?.invitation;
                      const orderTemplate = firstItem?.template;
                      const itemCount = order.items?.length || 0;
                      const status = order.status || 'pending';
                      return (
                        <motion.li
                          key={order.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: Math.min(index, 9) * 0.05 }}
                          className="bg-white border border-[#E6E1D6] rounded-[18px] p-4 sm:p-5"
                        >
                          <div className="flex gap-4 sm:gap-5 items-center">
                            {orderTemplate && (
                              <img
                                src={orderTemplate.thumbnail_image || orderTemplate.background_image}
                                alt=""
                                loading="lazy"
                                className="w-14 sm:w-16 aspect-[8.5/11] object-cover rounded-[3px] shrink-0 shadow-[0_8px_16px_-8px_rgba(26,26,26,0.4)]"
                              />
                            )}
                            <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                                  <h2 className="font-bold text-base sm:text-lg text-ink truncate max-w-full">
                                    {orderInvitation?.name || 'Invitation'}
                                    {itemCount > 1 && (
                                      <span className="text-[#6B665C] text-sm ml-2 font-normal">+{itemCount - 1} more</span>
                                    )}
                                  </h2>
                                  <StatusChip className={ORDER_CHIP[status] || CHIP.neutral}>
                                    {status === 'pending' ? 'Waiting for payment' : capitalize(status)}
                                  </StatusChip>
                                </div>
                                <p className="text-sm text-[#6B665C] mt-1">
                                  {orderNumberLabel(order.order_numbers) && (
                                    <span className="font-medium text-ink" data-testid={`order-numbers-${order.id}`}>
                                      {orderNumberLabel(order.order_numbers)} ·{' '}
                                    </span>
                                  )}
                                  Digital download
                                  {order.source === 'ivr' ? ' · Made by phone' : ''}
                                  {order.created_at ? ` · ${formatDate(order.created_at)}` : ''}
                                </p>
                              </div>
                              <p className="font-serif text-xl font-semibold text-ink shrink-0">
                                {formatPrice(order.total_amount || 0)}
                              </p>
                            </div>
                          </div>
                        </motion.li>
                      );
                    })}
                  </ul>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
